import { useSelector } from "react-redux";
import { selectLicenceState } from "../store/reducers/licenceSlice";
import { RootState } from "../store/store";

const hasFullAccess = (status?: string | null): boolean =>
  status === "ACTIVE" || status === "TRIAL";

/**
 * True when the facility has full feature access: an ACTIVE (paid)
 * licence, or a TRIAL licence — per LicenceStatus.java, TRIAL grants "full
 * feature access, no payment due until the trial ends", the same access
 * level the backend's LicenceGuard already grants (requireActiveLicence only
 * blocks when status isn't ACTIVE or TRIAL). PENDING_PAYMENT/EXPIRED/
 * PAUSED/CANCELLED remain restricted.
 *
 * MED-1687: a secretary cannot read the licence (manager-only BFF route), so
 * her facility's status comes from the session (MED-1688). No status, as for a
 * manager without a licence row, means no access.
 */
export const useHasLicence = (): boolean => {
  const { licence, errorCode } = useSelector(selectLicenceState);
  const session = useSelector((state: RootState) => state.session.session);

  if (session?.user?.roleName?.toLowerCase() === "secretary") {
    return hasFullAccess(session.metadata?.secretary?.healthcareFacility?.licenceStatus);
  }

  if (errorCode === "LICENCE_NOT_FOUND") return false;
  return hasFullAccess(licence?.status);
};
